/**
 * Mouse Movement Detection Strategy
 * Autonomous module that manages its own mouse event listeners and state
 * Tracks distance and angle to detect unnatural jumps and sharp turns
 *
 * Enhanced detection:
 * - Pre-click stillness (micro-movements before clicks)
 * - Entry point analysis (where mouse enters viewport)
 * - Velocity consistency
 */
import { BaseStrategy } from '../strategy.js';
import { calculateStatistics, gaussian } from '../math-utils.js';
export class MouseStrategy extends BaseStrategy {
    constructor(options) {
        super();
        this.name = 'mouse';
        this.defaultWeight = 0.30;
        this.distanceSeries = [];
        this.angleSeries = [];
        this.lastPosition = null;
        this.lastAngle = 0;
        this.cumulativeAngle = 0;
        this.rollingWindowMs = 30000;
        this.listener = null;
        this.leaveListener = null;
        this.enterListener = null;
        this.isActive = false;
        this.screenDiagonal = 1;
        /** Entry points tracking */
        this.entryPoints = [];
        /** Micro-movements in last 500ms (for stillness detection) */
        this.microMovements = [];
        /** Stillness window in ms */
        this.STILLNESS_WINDOW = 500;
        if ((options === null || options === void 0 ? void 0 : options.rollingWindow) !== undefined)
            this.rollingWindowMs = options.rollingWindow;
    }
    start() {
        if (this.isActive)
            return;
        this.isActive = true;
        // Calculate screen diagonal for normalization (distance relative to screen size)
        const width = window.innerWidth;
        const height = window.innerHeight;
        this.screenDiagonal = Math.sqrt(width * width + height * height);
        this.listener = (e) => {
            const mouseEvent = e;
            const now = Date.now();
            const currentPos = { x: mouseEvent.clientX, y: mouseEvent.clientY, timestamp: now };
            // Calculate distance and angle from previous position
            if (this.lastPosition) {
                const dx = currentPos.x - this.lastPosition.x;
                const dy = currentPos.y - this.lastPosition.y;
                const pixelDistance = Math.sqrt(dx * dx + dy * dy);
                // Track micro-movements (1-5px) for stillness detection
                if (pixelDistance >= 1 && pixelDistance <= 5) {
                    this.microMovements.push({ dx, dy, timestamp: now });
                    // Clean up old micro-movements (shift from front, more efficient than filter)
                    const cutoffMicro = now - this.STILLNESS_WINDOW;
                    while (this.microMovements.length > 0 && this.microMovements[0].timestamp < cutoffMicro) {
                        this.microMovements.shift();
                    }
                }
                // Normalize distance to screen diagonal (0-1 range)
                // A full diagonal movement = 1.0, a 10px movement on 2000px screen = 0.005
                const normalizedDistance = pixelDistance / this.screenDiagonal;
                // Only record if movement is meaningful (> 0.001 = ~2px on 2000px screen)
                if (normalizedDistance > 0.001) {
                    const rawAngle = Math.atan2(dy, dx); // -PI to PI
                    // Unwrap angle to avoid discontinuities (e.g., circles)
                    let angleDiff = rawAngle - this.lastAngle;
                    // Normalize difference to -PI to PI
                    while (angleDiff > Math.PI)
                        angleDiff -= 2 * Math.PI;
                    while (angleDiff < -Math.PI)
                        angleDiff += 2 * Math.PI;
                    this.cumulativeAngle += angleDiff;
                    this.lastAngle = rawAngle;
                    // Store normalized distance (not raw pixels)
                    this.distanceSeries.push({ value: normalizedDistance, timestamp: now });
                    this.angleSeries.push({ value: this.cumulativeAngle, timestamp: now });
                    // Notify detector for confidence tracking
                    this.notifyEvent(Math.min(1, normalizedDistance * 100));
                }
                // Apply rolling window efficiently: remove old events from start
                // Since events are chronological, we only check from the beginning
                const cutoff = now - this.rollingWindowMs;
                while (this.distanceSeries.length > 0 && this.distanceSeries[0].timestamp < cutoff) {
                    this.distanceSeries.shift();
                    this.angleSeries.shift(); // Keep both arrays in sync
                }
            }
            this.lastPosition = currentPos;
        };
        document.addEventListener('mousemove', this.listener, { passive: true });
        // Track mouse entry points into viewport
        this.enterListener = (e) => {
            const mouseEvent = e;
            const now = Date.now();
            const x = mouseEvent.clientX;
            const y = mouseEvent.clientY;
            const width = window.innerWidth;
            const height = window.innerHeight;
            // Calculate edge distances
            const distTop = y;
            const distBottom = height - y;
            const distLeft = x;
            const distRight = width - x;
            const minEdgeDist = Math.min(distTop, distBottom, distLeft, distRight);
            // Determine entry edge
            let entryEdge;
            const edgeThreshold = 50; // pixels from edge to count as edge entry
            if (minEdgeDist < edgeThreshold) {
                if (distTop <= minEdgeDist)
                    entryEdge = 'top';
                else if (distBottom <= minEdgeDist)
                    entryEdge = 'bottom';
                else if (distLeft <= minEdgeDist)
                    entryEdge = 'left';
                else
                    entryEdge = 'right';
            }
            else if (x > width * 0.35 && x < width * 0.65 &&
                y > height * 0.35 && y < height * 0.65) {
                entryEdge = 'center'; // Suspicious - bots often start at center
            }
            else if ((distTop < edgeThreshold * 2 && distLeft < edgeThreshold * 2) ||
                (distTop < edgeThreshold * 2 && distRight < edgeThreshold * 2) ||
                (distBottom < edgeThreshold * 2 && distLeft < edgeThreshold * 2) ||
                (distBottom < edgeThreshold * 2 && distRight < edgeThreshold * 2)) {
                entryEdge = 'corner';
            }
            else {
                // Entry from middle of an edge, normal
                if (distTop < distBottom && distTop < distLeft && distTop < distRight)
                    entryEdge = 'top';
                else if (distBottom < distLeft && distBottom < distRight)
                    entryEdge = 'bottom';
                else if (distLeft < distRight)
                    entryEdge = 'left';
                else
                    entryEdge = 'right';
            }
            this.entryPoints.push({
                x,
                y,
                timestamp: now,
                edgeDistance: minEdgeDist,
                entryEdge,
            });
            // Keep only last 20 entry points (shift is more memory efficient than slice)
            if (this.entryPoints.length > 20) {
                this.entryPoints.shift();
            }
        };
        document.addEventListener('mouseenter', this.enterListener, { passive: true });
        // Clear data when mouse leaves the window (discontinuous tracking)
        this.leaveListener = () => {
            this.distanceSeries = [];
            this.angleSeries = [];
            this.lastPosition = null;
            this.lastAngle = 0;
            this.cumulativeAngle = 0;
            this.microMovements = [];
        };
        document.addEventListener('mouseleave', this.leaveListener, { passive: true });
    }
    stop() {
        if (!this.isActive)
            return;
        this.isActive = false;
        if (this.listener) {
            document.removeEventListener('mousemove', this.listener);
            this.listener = null;
        }
        if (this.leaveListener) {
            document.removeEventListener('mouseleave', this.leaveListener);
            this.leaveListener = null;
        }
        if (this.enterListener) {
            document.removeEventListener('mouseenter', this.enterListener);
            this.enterListener = null;
        }
    }
    reset() {
        this.distanceSeries = [];
        this.angleSeries = [];
        this.lastPosition = null;
        this.lastAngle = 0;
        this.cumulativeAngle = 0;
        this.entryPoints = [];
        this.microMovements = [];
    }
    score() {
        if (this.distanceSeries.length < 10)
            return undefined;
        // Mouse-specific pattern detection (optimized for normalized continuous data)
        // Generic smoothness detector is calibrated for discrete events, not continuous movement
        return this.detectMousePatterns();
    }
    /**
     * Mouse-specific pattern detection
     * Detects bot-like patterns: constant velocity, linear paths, suspicious entry points
     */
    detectMousePatterns() {
        if (this.distanceSeries.length < 10)
            return undefined;
        let score = 0;
        let factors = 0;
        // 1. VELOCITY CONSISTENCY - Bots often move at constant speed
        const distances = this.distanceSeries.map(p => p.value);
        if (distances.length >= 3) {
            const stats = calculateStatistics(distances);
            // Real human movement has high variation (CV ~0.8-1.2)
            // page.mouse.move() with steps has lower CV (~0.4-0.6)
            // Narrower gaussian to be strict
            score += gaussian(stats.cv, 0.9, 0.35);
            factors++;
        }
        // 2. DIRECTION CHANGES - Bots often have too few or too many sharp turns
        const angles = this.angleSeries.map(p => p.value);
        if (angles.length >= 3) {
            const angleChanges = [];
            for (let i = 1; i < angles.length; i++) {
                angleChanges.push(Math.abs(angles[i] - angles[i - 1]));
            }
            const avgChange = angleChanges.reduce((a, b) => a + b, 0) / angleChanges.length;
            // Real humans have moderate but varied direction changes
            // page.mouse.move() linear interpolation has very small, consistent changes
            score += gaussian(avgChange, 0.15, 0.12);
            factors++;
        }
        // 3. ENTRY POINT ANALYSIS - Where does mouse enter the viewport?
        const entryScore = this.scoreEntryPoints();
        if (entryScore !== undefined) {
            score += entryScore;
            factors++;
        }
        // 4. MICRO-MOVEMENT PRESENCE - Humans have tremors, bots don't
        const microScore = this.scoreMicroMovements();
        if (microScore !== undefined) {
            score += microScore;
            factors++;
        }
        return factors > 0 ? score / factors : undefined;
    }
    /**
     * Score based on viewport entry points
     * Humans enter from edges with momentum; bots often start at (0,0) or center
     */
    scoreEntryPoints() {
        if (this.entryPoints.length < 2)
            return undefined;
        let suspiciousCount = 0;
        let centerCount = 0;
        let cornerCount = 0;
        let originCount = 0;
        for (const entry of this.entryPoints) {
            // Check for (0,0) or near-origin entries (very suspicious)
            if (entry.x < 5 && entry.y < 5) {
                originCount++;
                suspiciousCount++;
            }
            else if (entry.entryEdge === 'center') {
                centerCount++;
                suspiciousCount++;
            }
            else if (entry.entryEdge === 'corner') {
                cornerCount++;
                // Corners are slightly suspicious but not as bad as center
                if (entry.edgeDistance < 10)
                    suspiciousCount++;
            }
        }
        const total = this.entryPoints.length;
        const suspiciousRatio = suspiciousCount / total;
        // High ratio of origin entries = definitely bot
        if (originCount >= 2 || originCount / total >= 0.5)
            return 0.1;
        // High ratio of center entries = likely bot
        if (centerCount / total >= 0.5)
            return 0.2;
        // Mix of suspicious entries
        if (suspiciousRatio >= 0.7)
            return 0.3;
        if (suspiciousRatio >= 0.5)
            return 0.5;
        if (suspiciousRatio >= 0.3)
            return 0.7;
        // Mostly edge entries = human
        return 1.0;
    }
    /**
     * Score based on micro-movements (tremor detection)
     * Humans have natural hand tremor causing 1-5px jitter
     * Bots have perfect stillness or no micro-movements
     */
    scoreMicroMovements() {
        // Need some movement data first
        if (this.distanceSeries.length < 20)
            return undefined;
        const microCount = this.microMovements.length;
        // Calculate expected micro-movements based on tracking duration
        // Humans should have ~5-20 micro-movements per second when cursor is active
        const oldestEvent = this.distanceSeries[0];
        const newestEvent = this.distanceSeries[this.distanceSeries.length - 1];
        const durationMs = newestEvent.timestamp - oldestEvent.timestamp;
        if (durationMs < 1000)
            return undefined; // Need at least 1 second of data
        // Micro-movements per second
        const microPerSecond = (microCount / durationMs) * 1000;
        // No micro-movements at all = very suspicious
        if (microCount === 0)
            return 0.3;
        // Very few micro-movements = somewhat suspicious
        if (microPerSecond < 1)
            return 0.5;
        // Some micro-movements = more human-like
        if (microPerSecond < 5)
            return 0.7;
        // Good amount of micro-movements = human
        if (microPerSecond >= 5 && microPerSecond <= 30)
            return 1.0;
        // Excessive micro-movements (> 30/sec) might be artificial noise
        return 0.8;
    }
    /**
     * Get micro-movement count for external use (e.g., by ClickStrategy)
     */
    getMicroMovementCount() {
        return this.microMovements.length;
    }
    /**
     * Get last position for external use
     */
    getLastPosition() {
        return this.lastPosition ? { x: this.lastPosition.x, y: this.lastPosition.y } : null;
    }
    getDebugInfo() {
        return {
            eventCount: this.distanceSeries.length,
            rollingWindow: this.rollingWindowMs,
            isActive: this.isActive,
            distanceSeries: this.distanceSeries,
            angleSeries: this.angleSeries,
            entryPoints: this.entryPoints,
            microMovementCount: this.microMovements.length,
            lastPosition: this.lastPosition,
        };
    }
}
